import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { useLang } from "@/context/LangContext";
import { api } from "@/lib/api";
import SEO from "@/components/SEO";
import ImageCredit from "@/components/ImageCredit";
import { ArrowLeft, Leaf, Heart, ChefHat, Utensils, Info, Calendar } from "lucide-react";

const CATS = ["all", "agrumi", "ortaggi", "legumi", "pane", "condimenti", "miele", "latticini", "frutta_secca", "erbe"];

const currentMonth = () => new Date().getMonth() + 1;
const isInSeason = (p) => (p.season_months || []).includes(currentMonth());

const Prodotti = () => {
  const { slug } = useParams();
  const { t, lang } = useLang();
  const [prodotti, setProdotti] = useState([]);
  const [prod, setProd] = useState(null);
  const [filter, setFilter] = useState("all");
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/prodotti").then((r) => setProdotti(r.data)).catch(() => {});
    if (slug) {
      api.get(`/prodotti/${slug}`).then((r) => setProd(r.data)).catch(() => navigate("/prodotti"));
      window.scrollTo(0, 0);
    } else {
      setProd(null);
    }
  }, [slug, navigate]);

  if (slug && prod) {
    return (
      <div data-testid={`prodotto-detail-${slug}`}>
        <SEO
          titleIt={`${prod.name} — ${prod.it.tagline}`}
          titleEn={`${prod.name} — ${prod.en.tagline}`}
          descriptionIt={prod.it.description.slice(0, 300)}
          descriptionEn={prod.en.description.slice(0, 300)}
          image={prod.image}
          type="article"
        />
        <section className="relative pt-40 pb-20 min-h-[50vh] flex items-end overflow-hidden">
          <div className="absolute inset-0">
            <img loading="lazy" decoding="async" src={prod.image} alt="" className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-grafite via-grafite/50 to-grafite/10" />
            <ImageCredit src={prod.image} variant={prod.image_credit} size="lg" />
          </div>
          <div className="relative z-10 max-w-[1400px] mx-auto px-6 lg:px-12 w-full text-pietra-light">
            <Link to="/prodotti" className="inline-flex items-center gap-2 text-miele mb-6 hover:text-pietra-light">
              <ArrowLeft size={16} /> {t("nav_prodotti")}
            </Link>
            <div className="small-caps text-miele mb-3">{t(`cat_${prod.category}`)}</div>
            <h1 className="font-serif text-5xl md:text-7xl mb-4">{prod.name}</h1>
            <p className="text-xl md:text-2xl italic text-pietra-light/85 max-w-3xl">{prod[lang].tagline}</p>
          </div>
        </section>

        <section className="py-16 bg-pietra-light">
          <div className="max-w-[1200px] mx-auto px-6 lg:px-12 grid md:grid-cols-3 gap-12 items-start">
            <div className="md:col-span-2 space-y-10">
              <div>
                <div className="flex items-center gap-3 mb-3">
                  <Info size={20} className="text-carrubo" />
                  <h2 className="font-serif text-3xl text-grafite">{t("prodotti_tagline")}</h2>
                </div>
                <p className="text-lg text-grafite-soft leading-relaxed">{prod[lang].description}</p>
              </div>

              <div>
                <div className="flex items-center gap-3 mb-3">
                  <Heart size={20} className="text-carrubo" />
                  <h2 className="font-serif text-3xl text-grafite">{t("prodotti_salute")}</h2>
                </div>
                <ul className="space-y-2">
                  {prod[lang].salute.map((s, i) => (
                    <li key={i} className="flex items-start gap-3 text-grafite">
                      <span className="w-2 h-2 mt-2 bg-miele rounded-full flex-shrink-0" /><span>{s}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div>
                <div className="flex items-center gap-3 mb-3">
                  <Utensils size={20} className="text-carrubo" />
                  <h2 className="font-serif text-3xl text-grafite">{t("prodotti_usi")}</h2>
                </div>
                <p className="text-grafite-soft leading-relaxed">{prod[lang].usi}</p>
              </div>
            </div>

            <aside className="bg-pietra p-6 rounded-sm border border-carrubo/15 space-y-6">
              <div>
                <div className="small-caps text-miele mb-3 flex items-center gap-2"><Leaf size={14} /> {t("prodotti_areas")}</div>
                <ul className="space-y-2">
                  {prod.areas.map((a, i) => (
                    <li key={i} className="text-grafite font-medium">{a}</li>
                  ))}
                </ul>
              </div>
              {prod.season_months && (
                <div className="pt-6 border-t border-carrubo/10">
                  <div className="small-caps text-miele mb-3 flex items-center gap-2"><Calendar size={14} /> {t("prodotti_season")}</div>
                  <div className="text-sm text-grafite mb-4">{lang === "it" ? prod.season_it : prod.season_en}</div>
                  <div className="grid grid-cols-6 gap-1" data-testid="season-months">
                    {Array.from({ length: 12 }).map((_, i) => {
                      const m = i + 1;
                      const active = prod.season_months.includes(m);
                      const isNow = m === currentMonth();
                      return (
                        <div key={m} className={`h-8 rounded-sm flex items-center justify-center text-[0.55rem] font-semibold uppercase tracking-wider ${active ? (isNow ? "bg-miele text-grafite ring-2 ring-carrubo" : "bg-carrubo text-pietra-light") : "bg-pietra-light text-grafite-soft/40"}`}>
                          {t(`month_${m}`).slice(0, 3)}
                        </div>
                      );
                    })}
                  </div>
                  {isInSeason(prod) && (
                    <div className="mt-4 inline-flex items-center gap-2 bg-miele text-grafite text-xs font-bold uppercase tracking-widest px-3 py-2 rounded-sm">
                      <Leaf size={12} strokeWidth={2.4} /> {t("prodotti_in_season")}
                    </div>
                  )}
                </div>
              )}
            </aside>
          </div>
        </section>

        <section className="py-20 bg-pietra">
          <div className="max-w-[1200px] mx-auto px-6 lg:px-12">
            <div className="flex items-center gap-3 mb-10">
              <ChefHat size={22} className="text-carrubo" />
              <h2 className="font-serif text-3xl md:text-4xl text-grafite">{t("prodotti_ricette")}</h2>
            </div>
            <div className="grid md:grid-cols-3 gap-6">
              {prod[lang].ricette.map((r, i) => (
                <article key={i} data-testid={`ricetta-${i}`} className="p-8 bg-pietra-light rounded-sm border border-carrubo/10">
                  <div className="font-serif text-2xl text-carrubo mb-1">0{i + 1}</div>
                  <h3 className="font-serif text-xl text-grafite mb-4">{r.nome}</h3>
                  <p className="text-sm text-grafite-soft leading-relaxed">{r.descrizione}</p>
                </article>
              ))}
            </div>
          </div>
        </section>
      </div>
    );
  }

  const shown = filter === "all" ? prodotti : prodotti.filter((p) => p.category === filter);

  return (
    <div data-testid="prodotti-page">
      <SEO
        titleIt="Prodotti del Territorio — 18 eccellenze della Sicilia Orientale"
        titleEn="Local Products — 18 excellences of Eastern Sicily"
        descriptionIt="Diciotto prodotti tipici della Sicilia Orientale e dei Monti Iblei: Limone di Siracusa IGP, Pomodoro di Pachino IGP, Cioccolato di Modica, Ragusano DOP, Mandorla di Avola e altre eccellenze con ricette e stagionalità."
        descriptionEn="Eighteen typical products of Eastern Sicily and the Iblei Mountains: Siracusa Lemon PGI, Pachino Tomato PGI, Ragusano PDO cheese, Avola Almond and other excellences with recipes and seasonality."
      />
      <section className="pt-40 pb-16 bg-pietra-light border-b border-carrubo/10">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
          <div className="eyebrow mb-6">Sicilia Orientale · Iblei</div>
          <h1 className="font-serif text-5xl md:text-7xl text-grafite mb-4">{t("prodotti_title")}</h1>
          <p className="text-lg text-grafite-soft max-w-3xl mb-3">{t("prodotti_subtitle")}</p>
          <p className="text-xs text-grafite-soft italic opacity-70">{t("prodotti_disclaimer")}</p>
        </div>
      </section>

      <section className="py-8 bg-pietra sticky top-[72px] z-40 border-b border-carrubo/10">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 flex gap-2 overflow-x-auto">
          {CATS.map((c) => (
            <button key={c} data-testid={`filter-${c}`} onClick={() => setFilter(c)}
              className={`px-4 py-2 text-xs uppercase tracking-widest font-semibold rounded-sm whitespace-nowrap transition-all ${filter === c ? "bg-carrubo text-pietra-light" : "bg-pietra-light text-grafite hover:bg-carrubo/10"}`}>
              {t(`cat_${c}`)}
            </button>
          ))}
        </div>
      </section>

      <section className="py-16 bg-pietra-light">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
          {shown.map((p) => (
            <Link key={p.slug} to={`/prodotti/${p.slug}`} data-testid={`prodotto-card-${p.slug}`}
              className="group relative overflow-hidden bg-pietra rounded-sm border border-carrubo/15 hover:border-carrubo/60 transition-all">
              {isInSeason(p) && (
                <div className="absolute top-3 right-3 z-10 bg-miele text-grafite text-[0.55rem] font-bold uppercase tracking-widest px-2 py-1 rounded-sm flex items-center gap-1">
                  <Leaf size={10} strokeWidth={2.4} /> {t("prodotti_in_season")}
                </div>
              )}
              <div className="aspect-square overflow-hidden">
                <img loading="lazy" decoding="async" src={p.image} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
              </div>
              <div className="p-5">
                <div className="small-caps text-miele mb-2 text-[0.6rem]">{t(`cat_${p.category}`)}</div>
                <h3 className="font-serif text-lg text-grafite leading-tight mb-2 group-hover:text-carrubo">{p.name}</h3>
                <p className="text-xs text-grafite-soft italic line-clamp-2">{p[lang].tagline}</p>
              </div>
            </Link>
          ))}
        </div>
      </section>
    </div>
  );
};

export default Prodotti;
